import * as React from "react";
import { useState } from "react";
import { ChevronDown, Link2, Tags, UserCheck, Users } from "lucide-react";
import { Badge, Collapsible } from "@/components/ui";
import { cn } from "@/lib/utils";

const REASONS = [
  {
    icon: Link2,
    title: "The same link for everyone",
    text: "Paste the route into an email template or web page once. Every client who gets it uses that same link, and SocialCoach works out who they are when they click.",
  },
  {
    icon: UserCheck,
    title: "Reviews reach the right player",
    text: "Each response goes to the player who sent the email, and they're notified. When the form asks for public reviews, it shows that player's own Google, Zillow or Facebook links. Players with none connected collect the review on the form.",
  },
  {
    icon: Users,
    title: "One record per client",
    text: "We recognize clients you already have, so you don't end up with duplicates. Everything a client sends shows up on their Fan record in Fan Zone.",
  },
  {
    icon: Tags,
    title: "Know which emails get responses",
    text: "Give the route in each template a campaign name, and every response shows which email or page it came from.",
  },
];

type Need = "Recommended" | "Optional" | "If you have it";

const VALUES: { value: string; need: Need; does: string }[] = [
  {
    value: "Player email",
    need: "Recommended",
    does: "Sends each response to the right player. Without it, responses arrive with no player.",
  },
  {
    value: "Client ID",
    need: "Recommended",
    does: "Your CRM's ID for the client. The most reliable way to recognize them, even when their email changes, and it keeps personal details out of the link.",
  },
  { value: "Client email", need: "Optional", does: "Another way to recognize clients you already have." },
  { value: "Language", need: "Optional", does: "Opens the form in the client's language, when the form has it." },
  { value: "Campaign", need: "Optional", does: "Shows which email or page each response came from." },
  {
    value: "SocialCoach Fan ID",
    need: "If you have it",
    does: "Only if your CRM already stores our Fan IDs. Recognizes the client exactly.",
  },
];

/** Why routes exist, always shown; what to put in the link sits behind "See more". */
export function FormRoutesIntro() {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-sm flex flex-col gap-4"
      data-testid="form-routes-intro"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold text-slate-800">How Form Routes work</h2>
        <p className="text-sm text-slate-500">
          Put one link in an email journey or on your website. When a client clicks, SocialCoach opens the right form
          and connects the response to the right player and client.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {REASONS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-primary">
              <Icon className="size-4" />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-slate-800">{title}</span>
              <span className="text-xs text-slate-500 leading-relaxed">{text}</span>
            </span>
          </div>
        ))}
      </div>
      <Collapsible.Content className="flex flex-col gap-2 border-t border-slate-100 pt-4">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">What to include in the link</span>
        <table className="w-full text-sm" data-testid="intro-values">
          <tbody className="divide-y divide-slate-100">
            {VALUES.map(row => (
              <tr key={row.value}>
                <td className="py-2 pr-3 font-medium text-slate-700 whitespace-nowrap align-top">{row.value}</td>
                <td className="py-2 pr-3 align-top">
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] px-1.5 py-0 whitespace-nowrap",
                      row.need === "Recommended" && "border-primary/30 bg-primary/5 text-primary"
                    )}
                  >
                    {row.need}
                  </Badge>
                </td>
                <td className="py-2 text-slate-600">{row.does}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-slate-500">
          The link works even with nothing filled in: the client types their name and email on the form, and the
          response arrives with no player.
        </p>
      </Collapsible.Content>
      <Collapsible.Trigger className="group flex items-center gap-1 self-start text-sm font-semibold text-primary hover:underline cursor-pointer">
        {open ? "See less" : "See more"}
        <ChevronDown className="size-4 transition-transform group-data-[panel-open]:rotate-180" />
      </Collapsible.Trigger>
    </Collapsible>
  );
}
